import axios from "axios";
import { doc, onSnapshot } from "firebase/firestore";
import { useEffect, useRef, useState } from "react";
import {
  endAgentChatRequest,
  sendCustomerMsgToAgent,
} from "../api/firebase/live-chat.js";
import { locationMetaData } from "../api/metaData/meta-data.js";
import { updateChatInactivityTimer } from "../lib/helper/chatInactivityManager";
import { isValidEmail } from "../lib/utils.js";
import { getValidJWTToken } from "../services/jwtTokenService";
import db, { ensureAnonAuth } from "../utils/firebaseClient.js";
import { handleStream } from "../utils/handleStream.js";
import { logError } from "../utils/logger";

export const useChatbotController = (widgetKey, apiKey) => {
  const [chatHistory, setChatHistory] = useState([]);
  const [isChatCreate, setIsChatCreate] = useState(false);
  const [chatCreate, setChatCreate] = useState("");
  const [orgId, setOrgId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isRequestTypeAgent, setIsRequestTypeAgent] = useState("");
  const [currentAssistantChatMessage, setCurrentAssistantChatMessage] =
    useState("");
  const [isStreamComplete, setIsStreamComplete] = useState(false);
  const [chatSessionHistory, setChatSessionHistory] = useState([]);
  const [currentUserChatMessage, setCurrentUserChatMessage] = useState("");
  const [hasChatEnded, setHasChatEnded] = useState(false);
  const [chatValue, setChatValue] = useState("");
  const [liveChatsData, setLiveChatsData] = useState([]);
  const [step, setStep] = useState(1);
  const [isDisconnectChatLoading, setIsDisconnectChatLoading] = useState(false);
  const [location, setLocation] = useState("");
  const [userData, setUserData] = useState({
    customer_email: "",
    customer_name: "",
    message: "",
  });
  const [textareaHeight, setTextareaHeight] = useState(88);
  const [textareaMaximizeHeight, setTextareaMaximizeHeight] = useState(100);
  const [userAgentDetails, setUserAgentDetails] = useState("");

  const sendMessageContainerRef = useRef(null);

  // Call useBrandingApi hook
  const myHeaders = new Headers();
  myHeaders.append("Content-Type", "application/json");
  myHeaders.append("x-api-key", apiKey);

  const getAnswerRequestOptionsRaw = JSON.stringify({
    question: chatValue,
  });

  const getAnswerRequestOptions = {
    method: "POST",
    headers: myHeaders,
    body: getAnswerRequestOptionsRaw,
    redirect: "follow",
  };

  const handleStreamApi = async (chatCreate, getAnswerRequestOptions) => {
    setIsLoading(true);
    setIsStreamComplete(false);
    try {
      if (chatCreate !== "") {
        //Set focus to parent div of textarea since the textarea is disabled on message send
        //For accessibility
        sendMessageContainerRef?.current?.focus();

        // Get valid JWT token (cached or fetch new)
        const jwtToken = await getValidJWTToken();

        // Clone headers and add JWT token
        const headersWithJWT = new Headers(getAnswerRequestOptions.headers);
        headersWithJWT.set("Authorization", `Bearer ${jwtToken}`);

        const optionsWithJWT = {
          ...getAnswerRequestOptions,
          headers: headersWithJWT,
        };

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/chat-widget/${widgetKey}/${chatCreate}/question/stream`,
          optionsWithJWT,
        );

        const reader = response.body.getReader();
        await handleStream(reader, (data) => {
          if (data.type === "live_agent_request") {
            localStorage.setItem("oid", data.org_id);
            setOrgId(data.org_id);
            localStorage.setItem("typeAgent", "live_agent_request");
            setIsRequestTypeAgent("live_agent_request");
            setCurrentAssistantChatMessage(data.assistant);
            handleUserData();
            setCurrentUserChatMessage(chatValue);
          } else {
            const tokens = data.assistant; // Split the response into tokens
            setCurrentAssistantChatMessage(tokens);
            setCurrentUserChatMessage(chatValue);
          }
        });
      }
    } catch (error) {
      logError("Error in handleStreamApi:", error);
    } finally {
      setChatValue("");
      setTextareaHeight(88);
      setTextareaMaximizeHeight(100);
      setIsLoading(false);
      setIsStreamComplete(true); // Indicate the stream is complete
    }
  };

  const fetchLocation = async () => {
    try {
      // Ensure JWT token is valid (will refresh if needed)
      const jwtToken = await getValidJWTToken();

      const axiosInstance = axios.create({
        headers: {
          "Content-Type": "application/json",
          "x-api-key": apiKey,
          Authorization: `Bearer ${jwtToken}`,
        },
      });

      const response = await axiosInstance.get(
        `${process.env.REACT_APP_BACKEND_URL}/location`,
      );

      // const response = await axiosInstance.get(
      //   `${process.env.REACT_APP_BACKEND_URL}/location`
      // );

      const cityName = response.data.city.names.en;
      const stateName = response.data.subdivisions[0].names.en;
      const countryName = response.data.country.names.en;

      setLocation(`${cityName}, ${stateName}, ${countryName}`);
    } catch (err) {
      console.error("Error fetching location:", err);
    }
  };

  useEffect(() => {
    if (location !== "" && location !== undefined && location !== null) {
      postLocationData(chatCreate, location);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const postLocationData = async (chatId, locationData) => {
    const url = window.location.href || "unknown";
    // Ensure JWT token is valid (will refresh if needed)
    const jwtToken = await getValidJWTToken();

    const axiosInstance = axios.create({
      headers: {
        "Content-Type": "application/json",
        "x-api-key": apiKey,
        Authorization: `Bearer ${jwtToken}`,
      },
    });
    try {
      await locationMetaData(url, locationData, chatId, axiosInstance);
    } catch (err) {
      console.log(err);
    }
  };

  const handleChatCreate = async () => {
    setIsLoading(true);

    updateChatInactivityTimer();

    setChatSessionHistory((prev) => [
      ...prev,
      {
        user_message: currentUserChatMessage,
        assistant: currentAssistantChatMessage,
      },
    ]);

    setCurrentAssistantChatMessage("");
    setCurrentUserChatMessage("");

    const cId = localStorage.getItem("cid");

    if (chatValue.trim() === "") {
      setIsLoading(false);
      return;
    }

    try {
      if (!cId) {
        setIsChatCreate(false);
        setLocation("");
        //Set focus to parent div of textarea since the textarea is disabled on message send
        //For accessibility
        sendMessageContainerRef?.current?.focus();

        // Ensure JWT token is valid (will refresh if needed)
        const jwtToken = await getValidJWTToken();

        const requestOptionsRaw = JSON.stringify({
          name: chatValue,
        });

        // Clone headers and add JWT token
        const headersWithJWT = new Headers(myHeaders);
        headersWithJWT.set("Authorization", `Bearer ${jwtToken}`);

        const requestOptions = {
          method: "POST",
          headers: headersWithJWT,
          body: requestOptionsRaw,
          redirect: "follow",
        };

        // Get the Chat Id
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/chat-widget/${widgetKey}`,
          requestOptions,
        );

        const result = await response.json();
        const cid = result.cid ?? result.conversation_ref;
        setChatCreate(cid);
        await fetchLocation();
        setIsChatCreate(true);
        localStorage.setItem("cid", cid);
      } else {
        //Set focus to parent div of textarea since the textarea is disabled on message send
        //For accessibility
        sendMessageContainerRef?.current?.focus();

        // Get valid JWT token (cached or fetch new)
        const jwtToken = await getValidJWTToken();

        // Clone headers and add JWT token
        const headersWithJWT = new Headers(getAnswerRequestOptions.headers);
        headersWithJWT.set("Authorization", `Bearer ${jwtToken}`);

        const optionsWithJWT = {
          ...getAnswerRequestOptions,
          headers: headersWithJWT,
        };

        // Get the answer

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/chat-widget/${widgetKey}/${cId}/question/stream`,
          optionsWithJWT,
        );

        const reader = response.body.getReader();
        await handleStream(reader, (data) => {
          if (data.type === "live_agent_request") {
            setIsRequestTypeAgent("live_agent_request");
            handleUserData();
            localStorage.setItem("typeAgent", "live_agent_request");
            setCurrentAssistantChatMessage(data.assistant);
            setCurrentUserChatMessage(chatValue);
          } else {
            const tokens = data.assistant; // Split the response into tokens
            setCurrentAssistantChatMessage(tokens);
            setCurrentUserChatMessage(chatValue);
          }
        });
        setChatValue("");
        setTextareaHeight(88);
        setTextareaMaximizeHeight(100);
      }
    } catch (error) {
      logError("Error in handleChatCreate:", error);
    } finally {
      // fetchLocation();
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (step > 3) {
      ensureAnonAuth();
    }
  }, [step]);

  const handleUserData = () => {
    if (step === 1 && !userAgentDetails.is_chat_complete) {
      setLiveChatsData((pre) => [
        ...pre,
        { message: "Please enter your name", sender: "agent" },
      ]);
      setStep(2);
    } else if (step === 2) {
      if (chatValue !== "") {
        setUserData((prevData) => ({ ...prevData, customer_name: chatValue }));
        setLiveChatsData((pre) => [
          ...pre,
          { message: chatValue, sender: "customer" },
        ]);
        setLiveChatsData((pre) => [
          ...pre,
          { message: "Please enter your email", sender: "agent" },
        ]);
        setStep(3);
      }
    } else if (step === 3) {
      const isValid = isValidEmail(chatValue);

      if (!isValid && chatValue !== "") {
        setLiveChatsData((pre) => [
          ...pre,
          { message: chatValue, sender: "customer" },
        ]);
        setLiveChatsData((pre) => [
          ...pre,
          { message: "You have provided wrong email address", sender: "agent" },
        ]);
        setStep(3);
      } else if (chatValue !== "") {
        setUserData((prevData) => ({ ...prevData, customer_email: chatValue }));
        setLiveChatsData((pre) => [
          ...pre,
          { message: chatValue, sender: "customer" },
        ]);
        setLiveChatsData((pre) => [
          ...pre,
          { message: "Please enter your question", sender: "agent" },
        ]);
        setStep(4);
      }
    } else if (step === 4) {
      if (chatValue !== "") {
        setUserData((prevData) => ({ ...prevData, message: chatValue }));
        setLiveChatsData((pre) => [
          ...pre,
          { message: chatValue, sender: "customer" },
        ]);
        setLiveChatsData((pre) => [
          ...pre,
          {
            message: "We will connect to our agent shortly please wait.",
            sender: "agent",
          },
        ]);
        localStorage.setItem(
          "userDetails",
          JSON.stringify({ ...userData, message: chatValue }),
        );
        sendMessageToAgent(
          { ...userData, customer_location: location, message: chatValue },
          chatCreate,
        );
        setStep(5);
      }
    } else if (step === 5) {
      sendMessageToAgent({ message: chatValue }, chatCreate);
    }
    setChatValue("");
    setTextareaMaximizeHeight(100);
    setTextareaHeight(88);
  };

  const sendMessageToAgent = async (userData, chatId) => {
    try {
      updateChatInactivityTimer();

      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");
      myHeaders.append("x-api-key", apiKey);

      const getAnswerRequestOptionsRaw = JSON.stringify({
        ...userData,
      });

      const getAnswerRequestOptions = {
        method: "POST",
        headers: myHeaders,
        body: getAnswerRequestOptionsRaw,
        redirect: "follow",
      };

      await sendCustomerMsgToAgent(orgId, chatId, getAnswerRequestOptions);
    } catch (error) {
      console.log(error);
    }
  };

  const disconnectAgentChat = async (chatId) => {
    setIsDisconnectChatLoading(true);
    try {
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");
      myHeaders.append("x-api-key", apiKey);

      const getAnswerRequestOptions = {
        method: "POST",
        headers: myHeaders,
        redirect: "follow",
      };

      await endAgentChatRequest(orgId, chatId, getAnswerRequestOptions);
    } catch (error) {
      console.log(error);
    } finally {
      setIsDisconnectChatLoading(false);
    }
  };

  // // For first question following useEffect gets executed
  useEffect(() => {
    if (isChatCreate) {
      handleStreamApi(chatCreate, getAnswerRequestOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChatCreate]);

  useEffect(() => {
    setChatHistory(chatSessionHistory);
  }, [chatSessionHistory]);

  const getUserAgentDetails = (id, orgId) => {
    try {
      // Reference to the specific document in the 'chats' collection
      const docRef = doc(db, "orgs", orgId, "chats", id);

      const unsubscribe = onSnapshot(docRef, (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();

          setUserAgentDetails(data);
        } else {
          setUserAgentDetails(userAgentDetails);
        }
      });

      return () => unsubscribe(); // Clean up the subscription on unmount
    } catch (error) {
      console.error("Error in getUserAgentDetails:", error);

      return () => {
        // Empty cleanup function
      };
    }
  };

  return {
    isChatCreate,
    chatCreate,
    isLoading,
    isRequestTypeAgent,
    currentAssistantChatMessage,
    isStreamComplete,
    chatSessionHistory,
    currentUserChatMessage,
    hasChatEnded,
    chatValue,
    liveChatsData,
    step,
    sendMessageContainerRef,
    getAnswerRequestOptions,
    handleStreamApi,
    handleChatCreate,
    handleUserData,
    setChatValue,
    setCurrentUserChatMessage,
    setCurrentAssistantChatMessage,
    setLiveChatsData,
    setHasChatEnded,
    setChatCreate,
    setChatSessionHistory,
    setIsRequestTypeAgent,
    setStep,
    setChatHistory,
    chatHistory,
    disconnectAgentChat,
    isDisconnectChatLoading,
    setTextareaHeight,
    setTextareaMaximizeHeight,
    textareaHeight,
    textareaMaximizeHeight,
    userAgentDetails,
    getUserAgentDetails,
    setUserAgentDetails,
    setOrgId,
    orgId,
  };
};
